import React from "react"
import types from "prop-types"
import { shortMonthDate } from "utils/dateHelpers"
import {
  DAILY,
  DAILY_DESC,
  MONTHLY,
  monthlyDesc,
  WEEKLY,
  weeklyDesc,
} from "utils/recurrenceDetailsHelpers"
import { useDispatch } from "react-redux"
import { deleteScheduledReportExport } from "reduxSlices/reportIndexSlice"
import ItemRow from "shared/ItemRow"
import { CircularIconButton } from "shared/buttons"
import ScheduleActive from "./ScheduleActive"

const lastUploadDesc = (date) => `(last uploaded on ${shortMonthDate(date)})`
const lastFailedDesc = (date) => `(last attempt failed on ${shortMonthDate(date)})`

const scheduleDetails = (scheduledReportExportSchedule) => {
  switch (scheduledReportExportSchedule.frequency) {
  case DAILY:
    return DAILY_DESC
  case WEEKLY:
    return weeklyDesc(scheduledReportExportSchedule.recurrenceDayOfWeek)
  case MONTHLY:
    return monthlyDesc(scheduledReportExportSchedule.recurrenceDayOfMonth)
  default:
    return null
  }
}

const lastUploadDetails = (lastSuccessfulUpload, lastFailedUpload) => {
  if (!lastSuccessfulUpload && !lastFailedUpload) return "(no uploads yet)"

  if (lastSuccessfulUpload && !lastFailedUpload) {
    return lastUploadDesc(lastSuccessfulUpload)
  }

  if (!lastSuccessfulUpload && lastFailedUpload) {
    return lastFailedDesc(lastFailedUpload)
  }

  return lastSuccessfulUpload > lastFailedUpload ? lastUploadDesc(lastSuccessfulUpload) : lastFailedDesc(lastFailedUpload)
}

const ScheduledReportExportDetails = ({
  editScheduledReportExportPath, reportType, scheduledReportExport, reportId = null,
}) => {
  const {
    id: exportId, name, scheduledReportExportSchedule, lastFailedUpload, lastSuccessfulUpload,
  } = scheduledReportExport
  const dispatch = useDispatch()

  const { active: scheduleIsActive, id: scheduleId } = scheduledReportExportSchedule

  const goToSFTPSettingsEdit = () => {
    window.location.pathname = editScheduledReportExportPath(reportId, exportId)
  }

  const deleteSFTPSetting = () => {
    if (window.confirm("Are you sure you want to delete this SFTP uploads setting?")) {
      dispatch(deleteScheduledReportExport({ reportType, reportId, scheduledReportExportId: exportId }))
    }
  }

  return (
    <ItemRow
      className="dark"
      itemRowHeaderContent={(
        <span className="font-semibold">
          {name}
        </span>
      )}
      itemRowHeaderControls={(
        <>
          <CircularIconButton icon="pencil" onClick={goToSFTPSettingsEdit} ariaLabel="Edit SFTP setting" />
          <CircularIconButton icon="close" onClick={deleteSFTPSetting} ariaLabel="Delete SFTP setting" />
        </>
      )}
      itemRowBody={(
        <div className="flex-col divide-y divide-light-300 text-sm">
          <p className="pb-4">
            {scheduleDetails(scheduledReportExportSchedule)} {lastUploadDetails(lastSuccessfulUpload, lastFailedUpload)}
          </p>
          <div className="flex items-center justify-end gap-2 w-full pt-4">
            <span className="text-xs text-dark">
              {`SFTP uploads are ${scheduleIsActive ? "active" : "inactive"}`}
            </span>
            <ScheduleActive
              reportType={reportType}
              scheduleId={scheduleId}
            />
          </div>
        </div>
      )}
    />
  )
}

ScheduledReportExportDetails.propTypes = {
  reportId: types.number,
  reportType: types.string.isRequired,
  scheduledReportExport: types.objectOf(types.any).isRequired,
  editScheduledReportExportPath: types.func.isRequired,
}

export default ScheduledReportExportDetails
